//
import {
    getResourceForDataType,
    resourceGetList,
    resourcePatchDetail,
    resourcePostList,
    sendAction,
} from "shared/djangio/action-creators"
import * as actionTypes from "shared/profiles/position/action-types"
import { detailFromListRequest } from "shared/imports/sharedHelperFunctions"
import { STANCE_FILTER_KEY, PRIORITY_FILTER_KEY } from "shared/imports/sharedConstants"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

/**
 * Get the Alignment related to this Profile.
 *
 * @name getAlignment
 * @function
 * @param {object} dataType - QuorumDataType of the item you want the Alignment of.
 * @param {number} profileId - The id of the item you want the Alignment of.
 * @returns {object} The response of the request
 */
export const getAlignment = (dataType, profileId, actionDict = actionTypes.getAlignmentTypes, payload) =>
    resourceGetList(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionDict,
        {
            [dataType.related_field]: profileId,
            issue: null,
            effective_end_date: null,
            dehydrate_extra: ["username"],
        },
        { dataType, profileId, ...payload },
    )

/**
 * Change or remove this Profiles Alignment.
 *
 * @name setAlignment
 * @function
 * @param {object} alignment The AlignmentAction to be optimistically updated in-memory while the request resolves.
 * @param {object} dataType The QDT of the Profile being updated.
 * @param {number} profileId The ID of the Profile being updated.
 * @param {number} value The new AlignmentType to be set on the Profile.
 * @returns {object} the response of the request
 */
export const setAlignment = ({
    alignment,
    dataType,
    profileId,
    value,
    actionDict = actionTypes.setAlignmentTypes,
    payload,
}) => {
    const alignmentType = DjangIO.app.userdata.stakeholder.models.AlignmentType.resourceUriFromId(value)
    // If the new value is the same as the existing Alignment, remove it
    if (alignmentType === alignment.alignment_type) {
        return sendAction(DjangIO.app.userdata.stakeholder.models.AlignmentAction, actionDict, {
            action: "remove_alignment",
            kwargs: {
                [dataType.related_pk_field]: profileId,
            },
            method: "get",
            payload: {
                alignment,
                dataType,
                profileId,
                value,
                ...payload,
            },
        })
    } else {
        return resourcePostList(
            DjangIO.app.userdata.stakeholder.models.AlignmentAction,
            actionDict,
            {
                [dataType.related_field]: DjangIO.newResourceFromDataType(dataType.value).resourceUriFromId(profileId),
                alignment_type: alignmentType,
            },
            {
                alignment,
                dataType,
                profileId,
                value: alignmentType,
                ...payload,
            },
        )
    }
}

/**
 * Get the stance related to this bill
 *
 * @name getStance
 * @function
 * @param {number} dataType - QuorumDataType of the item you want a stance on
 * @param {number} profileId - The id of the item you want a stance on
 * @returns {object} The response of the request
 */
export const getStance = (dataType, profileId, actionDict = actionTypes.getStanceTypes, payload) => {
    const filter = {
        [DjangIO.app.models.QuorumDataType.by_value(dataType).related_field]: profileId,
        estimated_count: true,
    }
    return resourceGetList(DjangIO.app.userdata.models.LegislativeStance, actionDict, filter, {
        dataType,
        profileId,
        ...payload,
    })
}

/**
 * Change the stance of a LegislativeStance object
 *
 * @name setStance
 * @function
 * @param {number} itemValue The enum value that the stance will be set to
 * @param {object} stance The stance object to be patched
 * @param {number} region The region of the object being prioritized
 * @returns {object} the response of the request
 */
export const setStance =
    ({ value, stance, profileId, region = undefined } = {}) =>
    (dispatch) => {
        // clicking the current stance will uncheck that stance (so set to zero)
        const itemValue = value !== stance.stance ? value : 0

        const cacheInvalidationParams = {
            endpoints: [DjangIO.newResourceFromDataType(stance.data_type).endpoint],
            // In the case the URL constaints a stance filter, clear the cache.
            // In the case the URL contains a list filter, also clear the cache.
            // This is so search terms in bill tracking projects on the mobile app
            // can update.
            shouldClearFn: (url) => url.includes(STANCE_FILTER_KEY) || url.includes("in_list"),
        }

        if (!stance.id) {
            // We must create (POST) this stance
            const stanceObject = {
                stance: itemValue,
                data_type: stance.data_type,
                [DjangIO.app.models.QuorumDataType.by_value(stance.data_type).related_field]:
                    DjangIO.resourceFromDataType(stance.data_type).foreignKey(stance.profileId),
                last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
            }

            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.stance_taken,
                quorumDataType: stance.data_type,
                regions: [region],
            })

            return dispatch(
                resourcePostList(
                    DjangIO.app.userdata.models.LegislativeStance,
                    actionTypes.setStanceTypes,
                    stanceObject,
                    {
                        stance: stanceObject,
                        value: itemValue,
                        profileId,
                    },
                    { cacheInvalidationParams },
                ),
            )
        } else {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.stance_updated,
                quorumDataType: stance.data_type,
                regions: [region],
            })

            // We must update (PATCH) this stance
            return dispatch(
                resourcePatchDetail(
                    DjangIO.app.userdata.models.LegislativeStance,
                    actionTypes.setStanceTypes,
                    stance.id,
                    {
                        stance: itemValue,
                        archived: false,
                        last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                    },
                    { stance, value: itemValue, profileId },
                    { cacheInvalidationParams },
                ),
            )
        }
    }

/**
 * Conditionally PATCH or POST a LegislativeStance object. This function wraps getStance and setStance.
 *
 * @name setOrCreateStance
 * @function
 * @param {number} stance - The enum value that the stance will be set to
 * @param {number} qdt - The enum value of the model that the LegislativeStance is being set on (Bill, Document, etc.)
 * @param {id} id - The id of the object that the LegislativeStance is being set on (the foreign keyed object of the stance)
 */
export const setOrCreateStance =
    ({ stance, quorum_data_type, object_id }) =>
    (dispatch) =>
        // Fetch the stance object if it exists.
        dispatch(getStance(quorum_data_type, object_id)).then((response) => {
            const updateValue = {
                ...(detailFromListRequest(response) || {}),
                data_type: quorum_data_type,
                profileId: object_id,
            }
            // Construct the dictionary to PATCH or POST
            // Set the stance.
            return dispatch(
                setStance({
                    value: stance,
                    stance: updateValue,
                    profileId: object_id,
                }),
            )
        })

/**
 *
 * @name getPriority
 * @function
 * @param {number} billId - The id of the item you want a priority on
 * @returns {object} The response of the request
 */
export const getPriority = (dataType, profileId, actionDict = actionTypes.getPriorityTypes, payload) => {
    const filter = {
        [DjangIO.app.models.QuorumDataType.by_value(dataType).related_field]: profileId,
        estimated_count: true,
    }
    return resourceGetList(DjangIO.app.userdata.models.LegislativePriority, actionDict, filter, {
        dataType,
        profileId,
        ...payload,
    })
}

/**
 * Change the stance of a LegislativePriority object
 *
 * @name setPriority
 * @function
 * @param {number} itemValue The enum value that the priority will be set to
 * @param {object} priority The priority object to be patched
 * @param {number} region The region of the object being prioritized
 * @returns {object} the response of the request
 */
export const setPriority =
    ({ value, priority, profileId, region = undefined } = {}) =>
    (dispatch) => {
        // clicking the current priority will uncheck that priority (so set to zero)
        const itemValue = value !== priority.priority ? value : 0

        const cacheInvalidationParams = {
            endpoints: [DjangIO.newResourceFromDataType(priority.data_type).endpoint],
            shouldClearFn: (url) => url.includes(PRIORITY_FILTER_KEY), // this should be my_priority
        }

        if (!priority.id) {
            // We must create (POST) this priority

            const priorityObject = {
                priority: itemValue,
                data_type: priority.data_type,
                [DjangIO.app.models.QuorumDataType.by_value(priority.data_type).related_field]:
                    DjangIO.newResourceFromDataType(priority.data_type).foreignKey(priority.profileId),
                last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
            }

            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.priority_taken,
                quorumDataType: priority.data_type,
                regions: [region],
            })

            return dispatch(
                resourcePostList(
                    DjangIO.app.userdata.models.LegislativePriority,
                    actionTypes.setPriorityTypes,
                    priorityObject,
                    { priority: priorityObject, value: itemValue, profileId },
                    { cacheInvalidationParams },
                ),
            )
        } else {
            // We must update (PATCH) this priority
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.priority_updated,
                quorumDataType: priority.data_type,
                regions: [region],
            })

            return dispatch(
                resourcePatchDetail(
                    DjangIO.app.userdata.models.LegislativePriority,
                    actionTypes.setPriorityTypes,
                    priority.id,
                    {
                        priority: itemValue,
                        archived: false,
                        last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                    },
                    { priority, value: itemValue, profileId },
                    { cacheInvalidationParams },
                ),
            )
        }
    }

/**
 * Conditionally PATCH or POST a LegislativePriority object. This function wraps getPriority and setPriority.
 *
 * @name setOrCreatePriority
 * @function
 * @param {number} priority - The enum value that the priority will be set to
 * @param {id} object_id - The id of the object that the LegislativePriority is being set on (the foreign keyed object of the stance)
 * @param {number} quorum_data_type - The enum value that the of the data type you are setting the priority on
 */
export const setOrCreatePriority =
    ({ priority, object_id, quorum_data_type }) =>
    (dispatch) =>
        // Fetch the stance object if it exists.
        dispatch(getPriority(quorum_data_type, object_id)).then((response) => {
            // Construct the dictionary to PATCH or POST
            const updateValue = {
                ...(detailFromListRequest(response) || {}),
                data_type: quorum_data_type,
                profileId: object_id,
            }
            // Set the stance.
            return dispatch(
                setPriority({
                    value: priority,
                    priority: updateValue,
                    profile_id: object_id,
                }),
            )
        })

/**
 * POST (create) a Review object
 *
 * @name createReview
 * @function
 * @param {number} dataType - The enum value of the model that will be associated with the review
 * @param {id} datumId - ID of object to be associated with the review of type dataType
 * @param {id} userId - ID of the user to be associated with the review
 * @param {boolean} beOptimistic - whether we should optimistically update the store at the start of action
 */
export const createReview = ({ dataType, datumId, userId, beOptimistic }) => {
    const dataModel = getResourceForDataType(dataType)
    const qdt = DjangIO.app.models.QuorumDataType.by_value(dataType)
    return resourcePostList(
        DjangIO.app.userdata.review.models.Review,
        actionTypes.postReviewActionDict,
        {
            [qdt.related_field]: dataModel.foreignKey(datumId),
            user: DjangIO.app.userdata.models.QuorumUser.foreignKey(userId),
        },
        { profileId: datumId, dataType, beOptimistic },
    )
}

/**
 * try to GET Review object and POST (create) a Review object if none exist with associated fields
 *
 * @name getOrCreateReview
 * @function
 * @param {number} dataType - The enum value of the model that is associated with the review
 * @param {id} datumId - ID of object associated with the review of type dataType
 * @param {id} userId - ID of the user associated with the review
 */
export const getOrCreateReview =
    ({ dataType, datumId, userId }) =>
    async (dispatch) => {
        const qdt = DjangIO.app.models.QuorumDataType.by_value(dataType)
        // get reviews by filtering by user and related ID
        const response = await dispatch(
            resourceGetList(
                DjangIO.app.userdata.review.models.Review,
                actionTypes.getReviewActionDict,
                {
                    [qdt.related_field]: datumId,
                    user: userId,
                },
                { profileId: datumId, dataType },
            ),
        )
        const review = response.objects ? response.objects.length && response.objects[0] : response

        // If there is no review for user and datum, then create one
        if (!review) {
            return dispatch(
                createReview({
                    dataType,
                    datumId,
                    userId,
                    beOptimistic: false,
                }),
            )
        }
        return response
    }

/**
 * send action to PATCH Review object to delete it. DELETE requests are dissalowed throughout Quorum
 *
 * @name deleteReview
 * @function
 * @param {number} dataType - The enum value of the model that is associated with the review
 * @param {id} datumId - ID of object associated with the review of type dataType
 * @param {id} reviewId - ID of review to delete
 */
export const deleteReview = ({ dataType, datumId, reviewId }) =>
    sendAction(DjangIO.app.userdata.review.models.Review.objects.get(reviewId), actionTypes.patchReviewActionDict, {
        action: "delete_review",
        kwargs: {},
        method: "patch",
        payload: { reviewId, profileId: datumId, dataType },
    })
